/**
 * Model selection helpers — keep saved/cookie model ids in sync with backend config.
 */
import { getApiBaseUrl } from './api';

/** Last-resort default when /models cannot be fetched (matches backend DEFAULT_MODEL_ID). */
export const FALLBACK_DEFAULT_MODEL_ID = 'us.anthropic.claude-sonnet-5';

/** Opus 5.5's model id -- shared so App.js's new-conversation reset (only
 * resets away from Opus, see resetSelectedModelForNewConversation) and
 * ChatInterface.js's cost-confirmation modal stay in sync on one value. */
export const OPUS_5_5_MODEL_ID = 'us.anthropic.claude-opus-5-5';

let cachedConfig = null;

/**
 * @returns {Promise<{ models: Array<{id: string, name: string}>, defaultModelId: string }>}
 */
export async function fetchModelConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }

  const apiBase = getApiBaseUrl();
  const response = await fetch(`${apiBase}/models`);
  if (!response.ok) {
    throw new Error('Failed to fetch available models');
  }

  const data = await response.json();
  const models = Array.isArray(data) ? data : (data.models || []);
  const defaultModelId = Array.isArray(data)
    ? (models[0]?.id || FALLBACK_DEFAULT_MODEL_ID)
    : (data.default_model_id || models[0]?.id || FALLBACK_DEFAULT_MODEL_ID);

  cachedConfig = { models, defaultModelId };
  return cachedConfig;
}

/**
 * Return modelId when it is still available; otherwise defaultModelId.
 */
export function resolveSelectedModel(modelId, models, defaultModelId) {
  const availableIds = new Set((models || []).map((model) => model.id));
  if (modelId && availableIds.has(modelId)) {
    return modelId;
  }
  return defaultModelId || FALLBACK_DEFAULT_MODEL_ID;
}

/**
 * New-conversation model reset: only ever resets AWAY from Opus 5.5 (an
 * expensive, deliberately opt-in model -- see ChatInterface.js's own
 * cost-confirmation modal before switching TO it) back to whichever model
 * was in use right before that -- ``lastNonOpusModelId`` (e.g. GPT -> Opus ->
 * refresh lands back on GPT, not a hardcoded default). Falls back to
 * FALLBACK_DEFAULT_MODEL_ID only when there's no such model on record (e.g.
 * the very first session ever, somehow already on Opus). Any other model the
 * user was last on (Sonnet, GPT, Gemini, ...) carries over unchanged into a
 * new conversation -- only Opus's cost makes it worth not defaulting into by
 * accident.
 */
export function resetSelectedModelForNewConversation(currentModelId, lastNonOpusModelId) {
  if (currentModelId !== OPUS_5_5_MODEL_ID) return currentModelId;
  return lastNonOpusModelId || FALLBACK_DEFAULT_MODEL_ID;
}
